/**
 * MapView — thin wrapper around react-map-gl <Map> that:
 *   - Uses Mapbox + the configured token when REACT_APP_MAPBOX_TOKEN is set.
 *   - Falls back to MapLibre + free OSM raster tiles when no token is present.
 *   - Adds NavigationControl + GeolocateControl by default.
 *
 * Children (markers, layers, popups) are passed through. The `Marker`,
 * `Source`, `Layer` etc. components from `react-map-gl` work with both
 * backends because they're thin React adapters around the underlying map
 * instance — whichever instance the parent <Map> created.
 */
import React, { forwardRef } from 'react';
import { Map as MapboxMap, NavigationControl, GeolocateControl, MapRef, ViewState } from 'react-map-gl';
import { Map as MaplibreMap } from 'react-map-gl/maplibre';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  MAPBOX_TOKEN,
  MAPBOX_STYLE_STREETS,
  isMapboxConfigured,
  OSM_RASTER_STYLE,
} from '../../config/mapbox';

export interface MapViewProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  height?: number | string;
  width?: number | string;
  /** Mapbox style URL (only used when a Mapbox token is configured). */
  style?: string;
  interactive?: boolean;
  showNavigationControl?: boolean;
  showGeolocateControl?: boolean;
  initialViewState?: Partial<ViewState>;
  onClick?: (e: { lngLat: { lat: number; lng: number } }) => void;
  children?: React.ReactNode;
}

const MapView = forwardRef<MapRef, MapViewProps>(function MapView(
  {
    latitude,
    longitude,
    zoom = 13,
    height = 300,
    width = '100%',
    style = MAPBOX_STYLE_STREETS,
    interactive = true,
    showNavigationControl = true,
    showGeolocateControl = false,
    initialViewState,
    onClick,
    children,
  },
  ref,
) {
  const commonProps = {
    ref: ref as any,
    initialViewState: { latitude, longitude, zoom, ...initialViewState } as ViewState,
    style: { height, width },
    interactive,
    onClick: onClick as any,
    reuseMaps: true,
    attributionControl: true,
  };

  const controls = (
    <>
      {showNavigationControl && <NavigationControl position="top-right" />}
      {showGeolocateControl && (
        <GeolocateControl position="top-right" trackUserLocation showUserHeading />
      )}
      {children}
    </>
  );

  if (isMapboxConfigured()) {
    return (
      <MapboxMap {...commonProps} mapboxAccessToken={MAPBOX_TOKEN} mapStyle={style}>
        {controls}
      </MapboxMap>
    );
  }

  // Free OSM fallback — no token, no account. Renders raster tiles from
  // tile.openstreetmap.org with mandatory attribution.
  return (
    <MaplibreMap {...commonProps} mapStyle={OSM_RASTER_STYLE as any}>
      {controls}
    </MaplibreMap>
  );
});

export default MapView;

